import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getDropdownValues } from '../../services/moduleDataApi';
import { getFieldType } from './fieldTypeRegistry.jsx';

function normalizeOptions(options = []) {
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => {
      if (option === null || option === undefined) return null;
      if (typeof option === 'string' || typeof option === 'number') {
        return { label: String(option), value: option };
      }
      const label = option.label ?? option.name ?? option.value ?? option.id;
      const value = option.value ?? option.id ?? option.name ?? option.label;
      if (value === undefined) return null;
      // Preserved (not just label/value) so checkbox/radio can pre-select and
      // disable it — a remote source could plausibly carry this too, even
      // though today only the static admin-authored list does.
      return { label: String(label ?? value), value, locked: Boolean(option.locked) };
    })
    .filter(Boolean);
}

// Resolves a field's options once, for any registry type flagged needsOptions,
// so individual renderers never touch the network.
export default function SearchFieldControl({ field, moduleName, value, onChange, disabled }) {
  const definition = getFieldType(field.type ?? 'text');
  // A type may declare where its options come from (location → location master)
  // so an existing config that never set dataSource still resolves them.
  const dataSource = field.dataSource ?? definition.defaultDataSource ?? 'static';
  const isRemote = Boolean(definition.needsOptions) && dataSource !== 'static';

  const staticOptions = useMemo(
    () => normalizeOptions(field.options ?? field.values ?? []),
    [field.options, field.values],
  );
  const [remoteOptions, setRemoteOptions] = useState(null);
  // Seeded from isRemote rather than flipped inside the effect: a remote field
  // is loading from its first render, so there is nothing to announce later.
  const [optionsLoading, setOptionsLoading] = useState(isRemote);

  useEffect(() => {
    if (!isRemote) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const raw = await getDropdownValues(moduleName, field.field, '', 200, 0, {
          dataSource,
          masterName: field.masterName,
          groupName: field.groupName,
        });
        const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.values ?? []);
        if (!cancelled) setRemoteOptions(normalizeOptions(list));
      } catch (error) {
        console.error(`[SearchBuilderV1] Failed to load options for "${field.field}":`, error);
        if (!cancelled) setRemoteOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isRemote, dataSource, field.field, field.masterName, field.groupName, moduleName]);

  return definition.render({
    field,
    value,
    onChange: onChange ?? (() => {}),
    options: remoteOptions ?? staticOptions,
    optionsLoading,
    disabled,
    placeholder: field.placeholder,
    moduleName,
  });
}

SearchFieldControl.propTypes = {
  field: PropTypes.object.isRequired,
  moduleName: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};
